import { useAuth } from "@/src/contexts/auth.context";
import {
  CreateCommentPayload,
  CreatePostPayload,
  Post,
  ReactionType,
  ReactToCommentPayload,
  ReactToPostPayload,
  UpdatePostPayload,
  UpdatePostScorePayload,
} from "@/src/models/post.model";
import {
  COMMENTS_QUERY_KEY,
  POSTS_QUERY_KEY,
  REACTIONS_QUERY_KEY,
} from "@/src/query-keys/post.keys";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import { useGetUserProfileQuery } from "../../profile/profile.hooks";
import gqlClient from "../graphql.client";
import {
  CREATE_COMMENT_MUTATION,
  CREATE_POST_MUTATION,
  DELETE_POST_MUTATION,
  DELETE_REACTION_MUTATION,
  GET_COMMENTS_QUERY,
  GET_POSTS_QUERY,
  GET_REACTIONS,
  GET_USER_REACTION_QUERY,
  INSERT_REACTION_MUTATION,
  UPDATE_COMMENT_REACTION_COUNT_MUTATION,
  UPDATE_COMMENT_REPLY_COUNT_MUTATION,
  UPDATE_POST_COMMENT_COUNT_MUTATION,
  UPDATE_POST_MUTATION,
  UPDATE_POST_REACTION_COUNT_MUTATION,
  UPDATE_POST_SCORE_MUTATION,
  UPDATE_REACTION_MUTATION,
} from "./posts.query";
import apiClient from "../axios";

export const useGetPostsQuery = (groupId?: string) =>
  useInfiniteQuery({
    queryKey: groupId ? [POSTS_QUERY_KEY, groupId] : [POSTS_QUERY_KEY],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      return client.request(GET_POSTS_QUERY, {
        filter: groupId ? JSON.stringify({ groupId }) : "{}",
        order: [{ field: "score", direction: "DESC" }],
        pageNo: pageParam,
        pageSize: 10,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { hasNextPage, pageNo } = lastPage?.getposts ?? {};
      return hasNextPage ? pageNo + 1 : undefined;
    },
    staleTime: 1000 * 60 * 2,
  });

export const useGetPostByIdQuery = (
  postId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: [POSTS_QUERY_KEY, postId],
    queryFn: async () => {
      const client = await gqlClient();
      const result = await client.request(GET_POSTS_QUERY, {
        filter: JSON.stringify({ ItemId: postId }),
        sort: "{}",
        pageNo: 1,
        pageSize: 1,
      });
      return (result?.getposts?.items?.[0] as Post) ?? null;
    },
    enabled: options?.enabled ?? true,
  });
};

export const useCreatePostMutation = (groupId?: String) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: CreatePostPayload) => {
      const client = await gqlClient();
      return client.request(CREATE_POST_MUTATION, {
        content: payload.content,
        hashtags: payload.hashtags,
        mentionedUsers: payload.mentionedUsers?.map((user) => {
          const [firstName = "", ...rest] = (user.displayName ?? "").split(" ");
          return {
            firstName,
            lastName: rest.join(" "),
            userId: user.itemId,
            profileImageUrl: user.profileImageUrl,
            email: user.email,
            displayName: user.displayName,
          };
        }),
        visibility: payload.visibility ?? "public",
        attachmentUrls: payload.attachmentUrls ?? [],
        score: payload.score ?? 0,
        groupId: payload.groupId ?? null,
        visibleToGroupIds: payload.visibleToGroupIds ?? [],
        visibleToUserIds: payload.visibleToUserIds ?? [],
        ...payload.authorSnapshot,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupId ? [POSTS_QUERY_KEY, groupId] : [POSTS_QUERY_KEY] });
      Toast.show({ type: "success", text1: t("SUCCESS_POST_CREATED") });
    },
    onError: (error) => {
      Toast.show({ type: "error", text1: t("ERROR_CREATE_POST") });
      console.error(error);
    },
  });
};

export const useUpdatePostMutation = (groupId?: String) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const {user} = useAuth();

  return useMutation({
    mutationFn: async (payload: UpdatePostPayload) => {
      const client = await gqlClient();
      return client.request(UPDATE_POST_MUTATION, {
        filter: JSON.stringify({ ItemId: payload.id }),
        content: payload.content,
        hashtags: payload.hashtags,
        mentionedUsers: payload.mentionedUsers?.map((user) => {
          const [firstName = "", ...rest] = (user.displayName ?? "").split(" ");
          return {
            firstName,
            lastName: rest.join(" "),
            userId: user.itemId,
            profileImageUrl: user.profileImageUrl,
            email: user.email,
            displayName: user.displayName,
          };
        }),
        visibility: payload.visibility,
        attachmentUrls: payload.attachmentUrls ?? [],
        visibleToGroupIds: payload.visibleToGroupIds ?? [],
        visibleToUserIds: payload.visibleToUserIds ?? [],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupId ? [POSTS_QUERY_KEY, groupId] : [POSTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["USER_POSTS", user?.itemId]});
      Toast.show({ type: "success", text1: t("SUCCESS_POST_UPDATED") });
    },
    onError: (error) => {
      Toast.show({ type: "error", text1: t("ERROR_UPDATE_POST") });
      console.error(error);
    },
  });
};


export const useUpdatePostScoreMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: UpdatePostScorePayload) => {
      const client = await gqlClient();
      return client.request(UPDATE_POST_SCORE_MUTATION, {
        filter: JSON.stringify({ ItemId: payload.id }),
        score: payload.score ?? 0,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSTS_QUERY_KEY] });
    },
    onError: (error) => {
      Toast.show({ type: "error", text1: t("ERROR_UPDATE_POST") });
      console.error(error);
    },
  });
};

export const useDeletePostMutation = (groupId?: String) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const {user} = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const client = await gqlClient();
      return client.request(DELETE_POST_MUTATION, {
        filter: JSON.stringify({ ItemId: id }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupId ? [POSTS_QUERY_KEY, groupId] : [POSTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["USER_POSTS", user?.itemId]});
      Toast.show({ type: "success", text1: t("SUCCESS_POST_DELETED") });
    },
    onError: (error) => {
      Toast.show({ type: "error", text1: t("ERROR_DELETE_POST") });
      console.error(error);
    },
  });
};

export const useGetUserPostReaction = (postId: string, userId: string) => {
  return useQuery({
    queryKey: [REACTIONS_QUERY_KEY, postId, userId],
    queryFn: async () => {
      const client = await gqlClient();
      const res = await client.request(GET_USER_REACTION_QUERY, {
        filter: JSON.stringify({ postId, userId }),
      });
      const item = res.getreactions?.items?.[0];

      return {
        type: (item?.type as ReactionType) ?? null,
        id: item?.ItemId ?? null,
      };
    },
    enabled: !!postId && !!userId,
  });
};

export const useGetReactions = (postId: string) => {
  return useInfiniteQuery({
    queryKey: [REACTIONS_QUERY_KEY, postId],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      const result = await client.request(GET_REACTIONS, {
        filter: JSON.stringify({ postId }),
        pageNo: pageParam,
        pageSize: 10,
      });
      return result;
    },
    getNextPageParam: (lastPage) =>
      lastPage?.getreactions?.hasNextPage
        ? lastPage.getreactions.pageNo + 1
        : undefined,
    initialPageParam: 1,
  });
};

export const useReactToPostMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      postId,
      userId,
      newReaction,
      previousReaction,
      currentCounts,
      existingReactionId,
      reactorName,
      reactorProfileImageUrl
    }: ReactToPostPayload) => {
      const client = await gqlClient();
      let reactionId = existingReactionId;

      // 1. Update the Reaction collection
      if (!newReaction) {
        // Remove reaction
        await client.request(DELETE_REACTION_MUTATION, {
          filter: JSON.stringify({ postId, userId }),
        });
      } else if (existingReactionId) {
        // Switch reaction
        await client.request(UPDATE_REACTION_MUTATION, {
          filter: JSON.stringify({ ItemId: existingReactionId }),
          type: newReaction,
        });
      } else {
        // New reaction
        const res = await client.request(INSERT_REACTION_MUTATION, {
          userId,
          postId,
          type: newReaction,
          reactorName,
          reactorProfileImageUrl
        });
        reactionId = res.insertreaction.itemId;
      }

      // 2. Build updated counts
      const updatedCounts = { ...currentCounts };
      if (previousReaction) {
        updatedCounts[previousReaction] = Math.max(
          0,
          (updatedCounts[previousReaction] ?? 0) - 1,
        );
      }
      if (newReaction) {
        updatedCounts[newReaction] = (updatedCounts[newReaction] ?? 0) + 1;
      }

      // 3. Sync post reaction counts
      await client.request(UPDATE_POST_REACTION_COUNT_MUTATION, {
        filter: JSON.stringify({ ItemId: postId }),
        reactionCount: updatedCounts,
      });
      return { updatedCounts, reactionId };
    },

    onMutate: async ({
      postId,
      userId,
      newReaction,
      previousReaction,
      currentCounts,
    }) => {
      await queryClient.cancelQueries({ queryKey: POSTS_QUERY_KEY });
      await queryClient.cancelQueries({ queryKey: [POSTS_QUERY_KEY, postId] });
      await queryClient.cancelQueries({
        queryKey: [REACTIONS_QUERY_KEY, postId, userId],
      });

      const previousPosts = queryClient.getQueryData([POSTS_QUERY_KEY, ""]);
      const previousPost = queryClient.getQueryData([POSTS_QUERY_KEY, postId]);

      // Optimistically update counts on the post in cache
      queryClient.setQueryData([POSTS_QUERY_KEY, ""], (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            getposts: {
              ...page.getposts,
              items: page.getposts.items.map((post: any) => {
                if (post.ItemId !== postId) return post;
                const updatedCounts = { ...currentCounts };
                if (previousReaction)
                  updatedCounts[previousReaction] = Math.max(
                    0,
                    (updatedCounts[previousReaction] ?? 0) - 1,
                  );
                if (newReaction)
                  updatedCounts[newReaction] =
                    (updatedCounts[newReaction] ?? 0) + 1;
                return {
                  ...post,
                  reactionCount: updatedCounts,
                };
              }),
            },
          })),
        };
      });
      // Optimistically update the single post cache too
      queryClient.setQueryData([POSTS_QUERY_KEY, postId], (old: any) => {
        if (!old) return old;
        const updatedCounts = { ...currentCounts };
        if (previousReaction)
          updatedCounts[previousReaction] = Math.max(
            0,
            (updatedCounts[previousReaction] ?? 0) - 1,
          );
        if (newReaction)
          updatedCounts[newReaction] = (updatedCounts[newReaction] ?? 0) + 1;
        return {
          ...old,
          reactionCount: updatedCounts,
        };
      });

      // Optimistically update user reaction in cache
      queryClient.setQueryData(
        [REACTIONS_QUERY_KEY, postId, userId],
        (old: any) => ({
          id: old?.id ?? null,
          type: newReaction,
        }),
      );

      return { previousPosts, previousPost };
    },

    onSuccess: (data, { postId, userId, newReaction }) => {
      queryClient.setQueryData(
        [REACTIONS_QUERY_KEY, postId, userId],
        (old: any) => ({
          id: newReaction ? data.reactionId : null,
          type: newReaction ?? null,
        }),
      );
    },

    onError: (_err, { postId, userId }, context) => {
      queryClient.setQueryData([POSTS_QUERY_KEY, ""], context?.previousPosts);
      queryClient.setQueryData(
        [POSTS_QUERY_KEY, postId],
        context?.previousPost,
      );

      queryClient.setQueryData([REACTIONS_QUERY_KEY, postId, userId], null);
    },

    onSettled: (_data, err, { postId, userId }) => {
      if (err) {
        queryClient.invalidateQueries({ queryKey: [POSTS_QUERY_KEY] });
        queryClient.invalidateQueries({
          queryKey: [REACTIONS_QUERY_KEY, postId, userId],
        });
      }
    },
  });
};

// comments
export const useGetCommentsQuery = (
  postId: string,
  parentCommentId?: string,
) => {
  return useInfiniteQuery({
    queryKey: [COMMENTS_QUERY_KEY, postId, parentCommentId],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      return client.request(GET_COMMENTS_QUERY, {
        filter: JSON.stringify({ postId: postId }),
        parentCommentId: parentCommentId ?? null,
        pageNo: pageParam,
        pageSize: 10,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage?.getcomments?.hasNextPage
        ? lastPage.getcomments.pageNo + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!postId,
  });
};

export const useCreateCommentMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: CreateCommentPayload) => {
      const client = await gqlClient();

      // Create the comment
      const result = await client.request(CREATE_COMMENT_MUTATION, {
        postId: payload.postId,
        authorId: payload.authorId,
        parentCommentId: payload.parentCommentId ?? null,
        content: payload.content,
        ...payload.authorSnapshot,
        replyCount: payload.replyCount,
      });
      await client.request(UPDATE_POST_COMMENT_COUNT_MUTATION, {
        filter: JSON.stringify({ ItemId: payload.postId }),
        commentCount: (payload.postCommentCount ?? 0) + 1,
      });
      // If it's a reply, increment parent comment replyCount in DB
      if (payload.parentCommentId && payload.parentReplyCount !== undefined) {
        await client.request(UPDATE_COMMENT_REPLY_COUNT_MUTATION, {
          filter: JSON.stringify({ ItemId: payload.parentCommentId }),
          replyCount: payload.parentReplyCount + 1,
        });
      }

      return result;
    },

    onMutate: async (payload: CreateCommentPayload) => {
      await queryClient.cancelQueries({
        queryKey: [COMMENTS_QUERY_KEY, payload.postId, undefined],
      });

      const previousComments = queryClient.getQueryData([
        COMMENTS_QUERY_KEY,
        payload.postId,
        undefined,
      ]);

      // Optimistically increment replyCount on parent comment
      if (payload.parentCommentId) {
        queryClient.setQueryData(
          [COMMENTS_QUERY_KEY, payload.postId, undefined],
          (old: any) => {
            if (!old?.pages) return old;
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                getcomments: {
                  ...page.getcomments,
                  items: page.getcomments.items.map((comment: any) => {
                    if (comment.ItemId !== payload.parentCommentId)
                      return comment;
                    return {
                      ...comment,
                      replyCount: (comment.replyCount ?? 0) + 1,
                    };
                  }),
                },
              })),
            };
          },
        );
      }
      const previousPost = queryClient.getQueryData([
        POSTS_QUERY_KEY,
        payload.postId,
      ]);
      const previousPosts = queryClient.getQueryData([POSTS_QUERY_KEY, ""]);

      // Optimistically increment commentCount on single post cache
      queryClient.setQueryData(
        [POSTS_QUERY_KEY, payload.postId],
        (old: any) => {
          if (!old) return old;
          return { ...old, commentCount: (old.commentCount ?? 0) + 1 };
        },
      );

      // Optimistically increment commentCount on post list cache
      queryClient.setQueryData([POSTS_QUERY_KEY, ""], (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            getposts: {
              ...page.getposts,
              items: page.getposts.items.map((post: any) => {
                if (post.ItemId !== payload.postId) return post;
                return { ...post, commentCount: (post.commentCount ?? 0) + 1 };
              }),
            },
          })),
        };
      });

      return { previousComments, previousPost, previousPosts };
    },

    onError: (error, payload, context) => {
      queryClient.setQueryData(
        [COMMENTS_QUERY_KEY, payload.postId, undefined],
        context?.previousComments,
      );
      queryClient.setQueryData(
        [POSTS_QUERY_KEY, payload.postId],
        context?.previousPost,
      );
      queryClient.setQueryData([POSTS_QUERY_KEY, ""], context?.previousPosts);
      Toast.show({ type: "error", text1: t("ERROR_CREATE_COMMENT") });
      console.error(error);
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [COMMENTS_QUERY_KEY, variables.postId],
      });
      queryClient.invalidateQueries({
        queryKey: [POSTS_QUERY_KEY, variables.postId],
      });
      queryClient.invalidateQueries({
        queryKey: [POSTS_QUERY_KEY, ""],
      });

      Toast.show({ type: "success", text1: t("SUCCESS_SEND_COMMENT") });
    },
  });
};

// comment reactions
export const useGetUserCommentReaction = (
  commentId: string,
  userId: string,
) => {
  return useQuery({
    queryKey: [REACTIONS_QUERY_KEY, commentId, userId],
    queryFn: async () => {
      const client = await gqlClient();
      const res = await client.request(GET_USER_REACTION_QUERY, {
        filter: JSON.stringify({ commentId, userId }),
      });
      const item = res.getreactions?.items?.[0];
      return {
        type: (item?.type as ReactionType) ?? null,
        id: item?.ItemId ?? null,
      };
    },
    enabled: !!commentId && !!userId,
  });
};

export const useReactToCommentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      postId,
      userId,
      newReaction,
      previousReaction,
      currentCounts,
      existingReactionId,
    }: ReactToCommentPayload) => {
      const client = await gqlClient();

      let reactionId = existingReactionId;

      if (!newReaction) {
        await client.request(DELETE_REACTION_MUTATION, {
          filter: JSON.stringify({ commentId, userId }),
        });
      } else if (existingReactionId) {
        await client.request(UPDATE_REACTION_MUTATION, {
          filter: JSON.stringify({ ItemId: existingReactionId }),
          type: newReaction,
        });
      } else {
        const res = await client.request(INSERT_REACTION_MUTATION, {
          userId,
          postId,
          commentId,
          type: newReaction,
        });
        reactionId = res.insertreaction.itemId;
      }

      const updatedCounts = { ...currentCounts };
      if (previousReaction) {
        updatedCounts[previousReaction] = Math.max(
          0,
          (updatedCounts[previousReaction] ?? 0) - 1,
        );
      }
      if (newReaction) {
        updatedCounts[newReaction] = (updatedCounts[newReaction] ?? 0) + 1;
      }

      // Sync comment reaction counts
      await client.request(UPDATE_COMMENT_REACTION_COUNT_MUTATION, {
        filter: JSON.stringify({ ItemId: commentId }),
        reactionCount: updatedCounts,
      });

      return { updatedCounts, reactionId };
    },

    onMutate: async ({
      commentId,
      postId,
      userId,
      newReaction,
      previousReaction,
    }) => {
      await queryClient.cancelQueries({
        queryKey: [COMMENTS_QUERY_KEY, postId, undefined],
      });
      await queryClient.cancelQueries({
        queryKey: [REACTIONS_QUERY_KEY, commentId, userId],
      });

      const previousComments = queryClient.getQueryData([
        COMMENTS_QUERY_KEY,
        postId,
        undefined,
      ]);

      queryClient.setQueryData(
        [COMMENTS_QUERY_KEY, postId, undefined],
        (old: any) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              getcomments: {
                ...page.getcomments,
                items: page.getcomments.items.map((comment: any) => {
                  if (comment.ItemId !== commentId) return comment;

                  // read live counts from cache, not from prop
                  const liveCounts = { ...comment.reactionCount };
                  if (previousReaction)
                    liveCounts[previousReaction] = Math.max(
                      0,
                      (liveCounts[previousReaction] ?? 0) - 1,
                    );
                  if (newReaction)
                    liveCounts[newReaction] =
                      (liveCounts[newReaction] ?? 0) + 1;
                  return { ...comment, reactionCount: liveCounts };
                }),
              },
            })),
          };
        },
      );

      queryClient.setQueryData(
        [REACTIONS_QUERY_KEY, commentId, userId],
        (old: any) => ({
          id: old?.id ?? null,
          type: newReaction,
        }),
      );

      return { previousComments };
    },

    onSuccess: (data, { commentId, userId, newReaction }) => {
      queryClient.setQueryData(
        [REACTIONS_QUERY_KEY, commentId, userId],
        (old: any) => ({
          id: newReaction ? data.reactionId : null,
          type: newReaction ?? null,
        }),
      );
    },

    onError: (_err, { postId, commentId, userId }, context) => {
      queryClient.setQueryData(
        [COMMENTS_QUERY_KEY, postId],
        context?.previousComments,
      );
      queryClient.setQueryData([REACTIONS_QUERY_KEY, commentId, userId], null);
    },

    onSettled: (_data, err, { postId, commentId, userId }) => {
      if (err) {
        queryClient.invalidateQueries({
          queryKey: [COMMENTS_QUERY_KEY, postId],
        });
        queryClient.invalidateQueries({
          queryKey: [REACTIONS_QUERY_KEY, commentId, userId],
        });
      }
    },
  });
};

// query saved post
export const useSavedPostsQuery = (options?: { enabled?: boolean }) => {
  const { user } = useAuth();
  const userId = user?.itemId ?? "";

  const { data: userProfile } = useGetUserProfileQuery(userId);
  const savedPostIds = userProfile?.savedPostIds ?? [];

  return useQuery({
    queryKey: [POSTS_QUERY_KEY, "saved", userId],
    queryFn: async () => {
      if (!savedPostIds.length) return [];
      const client = await gqlClient();
      const result = await client.request(GET_POSTS_QUERY, {
        filter: JSON.stringify({ ItemId: { $in: savedPostIds } }),
        sort: JSON.stringify({}),
        pageNo: 1,
        pageSize: savedPostIds.length,
      });
      return (result?.getposts?.items as Post[]) ?? [];
    },
    enabled: (options?.enabled ?? true) && !!userId && !!userProfile,
  });
};

export const embedPost = async (payload: {
  post_id: string;
  content: string;
}) => {
  const res = await apiClient.post(`${process.env.EXPO_PUBLIC_EMBEDDING_SERVICE_URL}/posts`, payload);
  return res.data;
};


export const updateEmbeddedPost = async (payload: {
  post_id: string;
  content: string;
}) => {
  const res = await apiClient.put(`${process.env.EXPO_PUBLIC_EMBEDDING_SERVICE_URL}/posts/${payload.post_id}/`, payload);
  return res.data;
};


export const deleteEmbeddedPost = async (payload: {
  post_id: string;
}) => {
  const res = await apiClient.delete(`${process.env.EXPO_PUBLIC_EMBEDDING_SERVICE_URL}/posts/${payload.post_id}/`);
  return res.data;
};
// Get users posts
export const useGetUserPostsQuery = (userId: string) => {
  return useInfiniteQuery({
    queryKey: ["USER_POSTS", userId],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      const result = await client.request(GET_POSTS_QUERY, {
        filter: JSON.stringify({ CreatedBy: userId }),
        sort: JSON.stringify({ CreatedDate: -1 }),
        pageNo: pageParam,
        pageSize: 10,
      });
      return result;
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.getposts?.hasNextPage ? allPages.length + 1 : undefined,
    initialPageParam: 1,
    enabled: !!userId,
  });
};
